import React, { useState, useEffect } from 'react';
import {
    SegmentedControl,
    Card,
    Text,
    Group,
    Progress,
    Grid,
    Avatar,
    NumberInput,
    Checkbox,
    Badge,
    Button,
    Stack,
    Tooltip
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { GroupMember } from '../../../types/group';
import checkIcon from '../../../assets/icons/check.svg';
import itemsIcon from '../../../assets/icons/tag.svg';

interface SplitControlsProps {
    form: UseFormReturnType<any>;
    members: GroupMember[];
    setSplitTypeTouched: (value: boolean) => void;
    amountDebounced: number;
}

// Specific type for item in item-based split
interface SplitItem {
    id: string;
    name: string;
    amount: number;
    members: string[];
}

export const SplitControls = ({
    form,
    members,
    setSplitTypeTouched,
    amountDebounced
}: SplitControlsProps) => {
    const [splitItems, setSplitItems] = useState<SplitItem[]>([]);
    const [splitTotal, setSplitTotal] = useState(0);

    // Update split details when type or amount changes
    useEffect(() => {
        if (form.values.split_type) {
            updateSplitDetails(form.values.split_type);
        }
    }, [amountDebounced, form.values.split_type]);

    // Calculate splits based on split type
    const updateSplitDetails = (type: string, membersToSplit = members.filter(m =>
        // Include all non-removed members
        m.status !== 'removed'
    )) => {
        const amount = amountDebounced || Number(form.values.amount);
        const memberDetails: Record<string, number> = {};
        let calculatedTotal = 0;

        switch (type) {
            case 'equal':
                const perMember = membersToSplit.length > 0 ? parseFloat((amount / membersToSplit.length).toFixed(2)) : 0;
                membersToSplit.forEach((member, index) => {
                    // Handle rounding issues - add remainder to first member
                    let memberAmount = perMember;
                    if (index === 0) {
                        const expectedTotal = parseFloat((perMember * membersToSplit.length).toFixed(2));
                        const remainder = parseFloat((amount - expectedTotal).toFixed(2));
                        memberAmount = parseFloat((memberAmount + remainder).toFixed(2));
                    }

                    memberDetails[member.id] = memberAmount;
                    calculatedTotal += memberAmount;
                });
                break;

            case 'percentage':
                const defaultPercent = membersToSplit.length > 0 ? parseFloat((100 / membersToSplit.length).toFixed(2)) : 0;
                membersToSplit.forEach(member => {
                    const percentAmount = parseFloat((amount * defaultPercent / 100).toFixed(2));
                    memberDetails[member.id] = defaultPercent;
                    calculatedTotal += percentAmount;
                });
                break;

            case 'shares':
                const totalShares = membersToSplit.length;
                membersToSplit.forEach(member => {
                    memberDetails[member.id] = 1;
                    calculatedTotal += parseFloat((amount / totalShares).toFixed(2));
                });
                break;

            case 'exact':
                membersToSplit.forEach(member => {
                    memberDetails[member.id] = 0;
                });
                calculatedTotal = 0;
                break;

            case 'items':
                // For items split, initialize with empty object
                // The actual split will happen when items are added
                membersToSplit.forEach(member => {
                    memberDetails[member.id] = 0;
                });

                // Reset the items list
                setSplitItems([]);
                break;
        }

        setSplitTotal(calculatedTotal);
        setSplitTypeTouched(true);
        form.setFieldValue('split_type', type as any);
        form.setFieldValue('split_details', memberDetails);
    };

    // Add or update an item to the items-based split
    const handleAddItem = (item: SplitItem) => {
        const newItems = splitItems.filter(i => i.id !== item.id);
        const newItem = { ...item };
        newItems.push(newItem);
        setSplitItems(newItems);

        // Update the split_details field
        const newSplitDetails = { items: newItems };
        form.setFieldValue('split_details', newSplitDetails);

        // Recalculate the total for each member
        const memberAmounts: Record<string, number> = {};

        // Initialize all member amounts to 0
        members.filter(m => m.status === 'active' || m.status === 'ghost' || m.is_ghost === true).forEach(member => {
            memberAmounts[member.id] = 0;
        });

        // Calculate each member's share for each item
        newItems.forEach(item => {
            if (item.members.length === 0) return;

            const perMemberAmount = parseFloat((item.amount / item.members.length).toFixed(2));
            item.members.forEach(memberId => {
                memberAmounts[memberId] = (memberAmounts[memberId] || 0) + perMemberAmount;
            });
        });
    };

    // Remove an item from the items-based split
    const handleRemoveItem = (itemId: string) => {
        const newItems = splitItems.filter(item => item.id !== itemId);
        setSplitItems(newItems);

        const newSplitDetails = { items: newItems };
        form.setFieldValue('split_details', newSplitDetails);
    };

    // Render split input based on split type
    const renderSplitDetails = () => {
        const { split_type, split_details } = form.values;

        if (!split_details || Object.keys(split_details).length === 0) {
            return null;
        }

        let splitSum = 0;

        // Calculate the current total for percentage and exact splits
        if (split_type === 'percentage') {
            splitSum = Object.values(split_details).reduce((sum: number, value: any) => sum + Number(value), 0);
        } else if (split_type === 'exact') {
            splitSum = Object.values(split_details).reduce((sum: number, value: any) => sum + Number(value), 0);
        } else if (split_type === 'shares') {
            const totalShares = Object.values(split_details).reduce((sum: number, value: any) => sum + Number(value), 0);
            splitSum = totalShares > 0 ? parseFloat((Number(form.values.amount) * (totalShares / totalShares)).toFixed(2)) : 0;
        } else {
            splitSum = parseFloat((Object.values(split_details).reduce((sum: number, value: any) => sum + Number(value), 0)).toFixed(2));
        }

        const amountProgress = split_type === 'percentage'
            ? (splitSum / 100) * 100 // Percentage of 100%
            : (split_type === 'exact' ? (splitSum / Number(form.values.amount)) * 100 : 100); // Percentage of total amount

        return (
            <Card withBorder shadow="sm" padding="md" radius="md">
                <Text fw={600} size="sm">Split Details</Text>

                {(split_type === 'exact' || split_type === 'percentage') && (
                    <Group mt="xs">
                        <Progress
                            value={Math.min(amountProgress, 100)}
                            color={amountProgress > 100 ? 'red' : amountProgress === 100 ? 'green' : 'blue'}
                            size="sm"
                            radius="xl"
                            style={{ width: '100%' }}
                        />
                        <Group gap="xs">
                            <Text size="xs" c={amountProgress === 100 ? 'green' : (amountProgress > 100 ? 'red' : 'dimmed')}>
                                {split_type === 'percentage' ? `${splitSum.toFixed(1)}%` : `$${splitSum.toFixed(2)}`}
                            </Text>
                            <Text size="xs" c="dimmed">/</Text>
                            <Text size="xs" c="dimmed">
                                {split_type === 'percentage' ? '100%' : `$${Number(form.values.amount).toFixed(2)}`}
                            </Text>
                            {amountProgress === 100 && <img src={checkIcon} alt="Check" width={16} />}
                        </Group>
                    </Group>
                )}

                <Grid mt="sm">
                    {Object.keys(split_details).map(memberId => {
                        const member = members.find(m => m.id.toString() === memberId);
                        if (!member) return null;

                        let input;
                        switch (split_type) {
                            case 'exact':
                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Group>
                                            <NumberInput
                                                value={Number(split_details[memberId])}
                                                onChange={(value) => {
                                                    const newSplitDetails = { ...split_details };
                                                    newSplitDetails[memberId] = Number(value) || 0;
                                                    form.setFieldValue('split_details', newSplitDetails);
                                                }}
                                                min={0}
                                                step={0.01}
                                                style={{ width: '120px' }}
                                                rightSection={
                                                    <Tooltip label="Set to 0 to exclude this member from the split">
                                                        <Text size="xs" style={{ cursor: 'help' }}>?</Text>
                                                    </Tooltip>
                                                }
                                            />
                                        </Group>
                                    </Group>
                                );
                                break;

                            case 'percentage':
                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Group>
                                            <NumberInput
                                                value={Number(split_details[memberId])}
                                                onChange={(value) => {
                                                    const newSplitDetails = { ...split_details };
                                                    newSplitDetails[memberId] = Number(value) || 0;
                                                    form.setFieldValue('split_details', newSplitDetails);
                                                }}
                                                min={0}
                                                max={100}
                                                step={1}
                                                style={{ width: '90px' }}
                                                rightSection={
                                                    <Tooltip label="Set to 0 to exclude this member from the split">
                                                        <Text size="xs" style={{ cursor: 'help' }}>%</Text>
                                                    </Tooltip>
                                                }
                                            />
                                            <Text size="xs" c="dimmed">
                                                ${((Number(form.values.amount) * Number(split_details[memberId])) / 100).toFixed(2)}
                                            </Text>
                                        </Group>
                                    </Group>
                                );
                                break;

                            case 'shares':
                                const totalShares = Object.values(split_details).reduce((sum: number, value: any) => sum + Number(value), 0);
                                const shareAmount = totalShares > 0
                                    ? (Number(form.values.amount) * (Number(split_details[memberId]) / totalShares)).toFixed(2)
                                    : '0.00';

                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Group>
                                            <NumberInput
                                                value={Number(split_details[memberId])}
                                                onChange={(value) => {
                                                    const newSplitDetails = { ...split_details };
                                                    newSplitDetails[memberId] = Number(value) || 0;
                                                    form.setFieldValue('split_details', newSplitDetails);
                                                }}
                                                min={0}
                                                step={1}
                                                style={{ width: '90px' }}
                                                rightSection={
                                                    <Tooltip label="Set to 0 to exclude this member from the split">
                                                        <Text size="xs" style={{ cursor: 'help' }}>?</Text>
                                                    </Tooltip>
                                                }
                                            />
                                            <Text size="xs" c="dimmed">${shareAmount}</Text>
                                        </Group>
                                    </Group>
                                );
                                break;

                            default:
                                const splitAmount = Number(split_details[memberId]).toFixed(2);
                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Checkbox
                                                checked={Number(split_details[memberId]) > 0}
                                                onChange={(e) => {
                                                    const newSplitDetails = { ...split_details };
                                                    if (e.currentTarget.checked) {
                                                        // Add member to split
                                                        const activeMemberCount = Object.entries(newSplitDetails)
                                                            .filter(([_, value]) => Number(value) > 0).length + 1;
                                                        const perMember = parseFloat((Number(form.values.amount) / activeMemberCount).toFixed(2));

                                                        // Handle rounding issues by distributing the remainder
                                                        let appliedTotal = 0;

                                                        Object.keys(newSplitDetails).forEach(id => {
                                                            if (Number(newSplitDetails[id]) > 0 || id === memberId) {
                                                                newSplitDetails[id] = perMember;
                                                                appliedTotal += perMember;
                                                            }
                                                        });

                                                        // Add any remainder to the first active member
                                                        const remainder = parseFloat((Number(form.values.amount) - appliedTotal).toFixed(2));
                                                        if (remainder !== 0) {
                                                            for (const id of Object.keys(newSplitDetails)) {
                                                                if (Number(newSplitDetails[id]) > 0) {
                                                                    newSplitDetails[id] = parseFloat((Number(newSplitDetails[id]) + remainder).toFixed(2));
                                                                    break;
                                                                }
                                                            }
                                                        }
                                                    } else {
                                                        // Remove member from split
                                                        newSplitDetails[memberId] = 0;
                                                        const remainingMembers = Object.keys(newSplitDetails)
                                                            .filter(id => Number(newSplitDetails[id]) > 0);

                                                        if (remainingMembers.length > 0) {
                                                            const perMember = parseFloat((Number(form.values.amount) / remainingMembers.length).toFixed(2));

                                                            // Handle rounding issues by distributing the remainder
                                                            let appliedTotal = 0;

                                                            remainingMembers.forEach(id => {
                                                                newSplitDetails[id] = perMember;
                                                                appliedTotal += perMember;
                                                            });

                                                            // Add any remainder to the first active member
                                                            const remainder = parseFloat((Number(form.values.amount) - appliedTotal).toFixed(2));
                                                            if (remainder !== 0 && remainingMembers.length > 0) {
                                                                newSplitDetails[remainingMembers[0]] = parseFloat(
                                                                    (Number(newSplitDetails[remainingMembers[0]]) + remainder).toFixed(2)
                                                                );
                                                            }
                                                        }
                                                    }
                                                    form.setFieldValue('split_details', newSplitDetails);
                                                }}
                                            />
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Text size="sm" fw={500}>${splitAmount}</Text>
                                    </Group>
                                );
                        }

                        return (
                            <Grid.Col key={memberId} span={12}>
                                {input}
                            </Grid.Col>
                        );
                    })}
                </Grid>
            </Card>
        );
    };

    // Render item-based split UI
    const renderItemsSplit = () => {
        if (form.values.split_type !== 'items') return null;

        return (
            <Card withBorder shadow="sm" padding="md" radius="md">
                <Text fw={600} size="sm" mb="md">Items</Text>

                {splitItems.length > 0 ? (
                    <Stack gap="md">
                        {splitItems.map(item => {
                            const memberCount = item.members.length;
                            const perPersonAmount = memberCount > 0
                                ? (item.amount / memberCount).toFixed(2)
                                : '0.00';

                            return (
                                <Card key={item.id} p="xs" withBorder>
                                    <Group justify="space-between">
                                        <Text fw={500}>{item.name}</Text>
                                        <Group gap="xs">
                                            <Text>${item.amount.toFixed(2)}</Text>
                                            <Button
                                                size="xs"
                                                color="red"
                                                variant="subtle"
                                                onClick={() => handleRemoveItem(item.id)}
                                            >
                                                Remove
                                            </Button>
                                        </Group>
                                    </Group>

                                    <Group mt="xs" gap="xs">
                                        <Text size="xs">Split between:</Text>
                                        {item.members.map(memberId => {
                                            const member = members.find(m => m.id.toString() === memberId);
                                            return (
                                                <Badge key={memberId} size="sm">
                                                    {member?.display_name || member?.email || 'Unknown'}
                                                </Badge>
                                            );
                                        })}
                                    </Group>

                                    {memberCount > 0 && (
                                        <Text size="xs" mt="xs" c="dimmed">
                                            ${perPersonAmount} per person
                                        </Text>
                                    )}
                                </Card>
                            );
                        })}
                    </Stack>
                ) : (
                    <Text size="sm" c="dimmed" mb="md">No items added yet.</Text>
                )}

                {/* Add Item Button */}
                <Button
                    variant="light"
                    leftSection={<img src={itemsIcon} alt="" width={14} />}
                    mt="md"
                    onClick={() => {
                        // Open a dialog to add a new item
                        const itemName = window.prompt('Item name:');
                        if (!itemName) return;

                        const itemAmount = parseFloat(window.prompt('Item amount:', '0') || '0');
                        if (itemAmount <= 0) {
                            alert('Amount must be greater than 0');
                            return;
                        }

                        // Simple member selection for items
                        const selectedMembers: string[] = [];
                        members.filter(m => m.status === 'active' || m.status === 'ghost' || m.is_ghost === true).forEach(member => {
                            if (window.confirm(`Include ${member.display_name || member.email}?`)) {
                                selectedMembers.push(member.id.toString());
                            }
                        });

                        handleAddItem({
                            id: Date.now().toString(),
                            name: itemName,
                            amount: itemAmount,
                            members: selectedMembers,
                        });
                    }}
                    fullWidth
                >
                    Add Item
                </Button>
            </Card>
        );
    };

    return (
        <>
            <SegmentedControl
                value={form.values.split_type}
                onChange={(value) => updateSplitDetails(value)}
                data={[
                    { label: 'Equal', value: 'equal' },
                    { label: 'Exact', value: 'exact' },
                    { label: 'Percentage', value: 'percentage' },
                    { label: 'Shares', value: 'shares' },
                    { label: 'Items', value: 'items' },
                ]}
                fullWidth
                radius="md"
            />

            {form.values.split_type === 'items' ? renderItemsSplit() : renderSplitDetails()}
        </>
    );
}; 